import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Search, ShoppingBag, ChevronDown, Truck } from 'lucide-react';
import { LOGO_URL, CORPORATE_CATEGORIES, PERSONAL_CATEGORIES } from '../mock/mock';

const AnnouncementBar = () => (
  <div className="w-full bg-[#2C2416] text-[#FAF6F1] text-[12px] tracking-[0.14em] uppercase">
    <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
      <div className="hidden md:flex items-center gap-2">
        <Truck className="w-3.5 h-3.5" /> PAN India delivery on every hamper
      </div>
      <div className="flex-1 md:flex-none text-center md:text-left">
        We do Bulk &amp; Corporate Gifting too &nbsp;·&nbsp;
        <Link to="/corporate-gifting#enquire" className="underline underline-offset-4 hover:text-[#F4D5C5]">Enquire now</Link>
      </div>
      <a href="tel:+918860553236" className="hidden md:flex items-center gap-2 hover:text-[#F4D5C5]">
        <Phone className="w-3.5 h-3.5" /> +91 88605 53236
      </a>
    </div>
  </div>
);

const MegaMenu = ({ title, items, basePath }) => {
  const isCorporate = basePath.includes('corporate');
  return (
    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-40">
      <div className="mega-panel bg-white rounded-xl p-6 w-[760px] shadow-2xl border border-[#EFE3D2]">
        <div className="flex items-baseline justify-between mb-4">
          <h4 className="font-serif-display text-2xl text-[#2C2416]">{title}</h4>
          <Link to={basePath} className="text-xs uppercase tracking-widest text-[#7A5C4C] hover:text-[#C58B76]">View all →</Link>
        </div>

        {/* Featured Silo Quick Links */}
        <div className="flex items-center gap-2 pb-3 mb-3 border-b border-[#F0E7DA] text-xs">
          <span className="text-[#C58B76] font-semibold uppercase tracking-wider text-[10px]">Popular:</span>
          {isCorporate ? (
            <div className="flex flex-wrap gap-2">
              <Link to="/employee-welcome-kits" className="px-2.5 py-1 bg-[#FAF6F1] hover:bg-[#F4D5C5] rounded-md text-[#2C2416] transition-colors">
                Onboarding Kits
              </Link>
              <Link to="/client-gifting" className="px-2.5 py-1 bg-[#FAF6F1] hover:bg-[#F4D5C5] rounded-md text-[#2C2416] transition-colors">
                Client Hampers
              </Link>
              <Link to="/festive-hampers" className="px-2.5 py-1 bg-[#FAF6F1] hover:bg-[#F4D5C5] rounded-md text-[#2C2416] transition-colors">
                Festive Diwali
              </Link>
              <Link to="/corporate-gifting-delhi-ncr" className="px-2.5 py-1 bg-[#FAF6F1] hover:bg-[#F4D5C5] rounded-md text-[#2C2416] transition-colors">
                Delhi NCR
              </Link>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              <Link to="/wedding-return-gifts" className="px-2.5 py-1 bg-[#FAF6F1] hover:bg-[#F4D5C5] rounded-md text-[#2C2416] transition-colors">
                Wedding Return Gifts
              </Link>
              <Link to="/occasion-gifting" className="px-2.5 py-1 bg-[#FAF6F1] hover:bg-[#F4D5C5] rounded-md text-[#2C2416] transition-colors">
                Baby Shower &amp; Milestones
              </Link>
            </div>
          )}
        </div>

        <div className="grid grid-cols-3 gap-3">
          {items.map((c) => (
            <Link key={c.id} to={`/category/${isCorporate ? 'corporate' : 'personal'}/${c.id}`}
              className="group/item flex gap-3 p-2 rounded-md hover:bg-[#FAF6F1] transition-colors">
              <div className="w-14 h-14 rounded-md overflow-hidden flex-shrink-0">
                <img
                  src={c.hero}
                  alt={`${c.title} corporate gifts`}
                  width="56"
                  height="56"
                  loading="lazy"
                  className="w-full h-full object-cover group-hover/item:scale-105 transition-transform duration-500"
                />
              </div>
              <div>
                <div className="text-sm font-medium text-[#2C2416] leading-tight">{c.title}</div>
                <div className="text-xs text-[#7A5C4C] mt-0.5 leading-snug">{c.tagline}</div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <header className="sticky top-0 z-50">
      <div className={`bg-[#FAF6F1] border-b border-[#E9DFD3] transition-shadow ${scrolled ? 'shadow-[0_10px_30px_-15px_rgba(60,40,30,0.18)]' : ''}`}>
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between h-24">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Wrap Your Wish" className="h-20 w-20 object-contain" />
          </Link>

          <nav className="hidden lg:flex items-center gap-12">
            <Link to="/" className="text-base font-medium text-[#2C2416] hover:text-[#C58B76] transition-colors">Home</Link>
            <div className="group relative">
              <button className="text-base font-medium text-[#2C2416] hover:text-[#C58B76] transition-colors flex items-center gap-1">
                Corporate Gifting <ChevronDown className="w-3.5 h-3.5" />
              </button>
              <MegaMenu title="Corporate Gifting" items={CORPORATE_CATEGORIES} basePath="/corporate-gifting" />
            </div>
            <div className="group relative">
              <button className="text-base font-medium text-[#2C2416] hover:text-[#C58B76] transition-colors flex items-center gap-1">
                Personalised Gifting <ChevronDown className="w-3.5 h-3.5" />
              </button>
              <MegaMenu title="Personalised Gifting" items={PERSONAL_CATEGORIES} basePath="/personalised-gifting" />
            </div>
            <Link to="/case-studies" className="text-base font-medium text-[#2C2416] hover:text-[#C58B76] transition-colors">Client Stories</Link>
          </nav>

          <div className="flex items-center gap-3">
            <a href="#enquire" className="hidden md:inline-flex btn-primary px-4 py-2 text-xs uppercase tracking-widest rounded-full">
              Enquire Now
            </a>
            <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden w-9 h-9 flex items-center justify-center" aria-label="Menu">
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="lg:hidden border-t border-[#E9DFD3] bg-white">
            <div className="px-4 py-4 space-y-1">
              <Link to="/" className="block py-2.5 text-sm border-b border-[#F0E7DA]">Home</Link>
              <Link to="/corporate-gifting" className="block py-2.5 text-sm border-b border-[#F0E7DA] font-medium">Corporate Gifting Studio</Link>
              <div className="pl-3 py-1 space-y-1 border-b border-[#F0E7DA] text-xs text-[#7A5C4C]">
                <Link to="/employee-welcome-kits" className="block py-1 hover:text-[#C58B76]">→ Employee Welcome Kits</Link>
                <Link to="/client-gifting" className="block py-1 hover:text-[#C58B76]">→ Client Hampers</Link>
                <Link to="/festive-hampers" className="block py-1 hover:text-[#C58B76]">→ Festive &amp; Diwali Hampers</Link>
                <Link to="/corporate-gifting-delhi-ncr" className="block py-1 hover:text-[#C58B76]">→ Delhi NCR Studio</Link>
              </div>
              <Link to="/personalised-gifting" className="block py-2.5 text-sm border-b border-[#F0E7DA] font-medium">Personalised Gifting</Link>
              <div className="pl-3 py-1 space-y-1 border-b border-[#F0E7DA] text-xs text-[#7A5C4C]">
                <Link to="/wedding-return-gifts" className="block py-1 hover:text-[#C58B76]">→ Wedding Return Gifts</Link>
                <Link to="/occasion-gifting" className="block py-1 hover:text-[#C58B76]">→ Baby Shower &amp; Milestones</Link>
              </div>
              <Link to="/case-studies" className="block py-2.5 text-sm">Client Success Stories</Link>
              <a href="#enquire" className="btn-primary block text-center px-4 py-3 text-xs uppercase tracking-widest rounded-full mt-3">Enquire Now</a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
